import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { LogOut, ShieldCheck, Clock, Users } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  getEventoVisibleByIdParaSocio,
  hidratarEventosPublicos,
} from "@/data/adminEventosStore";
import {
  consultarEstadoFila,
  cancelarFila,
  heartbeatFila,
} from "@/data/smartQueueStore";
import { SMART_QUEUE } from "@/constants/testIds";

/**
 * Sprint 48 · Smart Queue REAL · consume backend (queue/status).
 *
 * - Si el backend responde `irASeleccionMesa=true`, navegar de forma
 *   inmediata a selección de mesa (no se renderiza UI de fila).
 * - Polling controlado por el `intervalPollingSugeridoSegundos` que
 *   devuelve el backend (5 s en espera / 10 s con turno activo).
 * - Heartbeat cada `heartbeatSegundos` cuando el turno está activo,
 *   para que la actividad natural del usuario no permita que el
 *   backend expire por inactividad.
 * - Sin `setInterval` colgado al desmontar. Sin localStorage.
 */
export default function SmartQueue() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [hidratado, setHidratado] = useState(false);
  const [estado, setEstado] = useState(null);
  const [errorRed, setErrorRed] = useState(false);
  const pollRef = useRef(null);
  const hbRef = useRef(null);
  const navegadoRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    hidratarEventosPublicos().finally(() => setHidratado(true));
  }, []);

  const evento = getEventoVisibleByIdParaSocio(id);

  const stopTimers = () => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
    if (hbRef.current) {
      clearInterval(hbRef.current);
      hbRef.current = null;
    }
  };

  const tick = async () => {
    if (!mountedRef.current || !evento) return;
    const res = await consultarEstadoFila(evento.id);
    if (!mountedRef.current) return;
    if (res && !res.error) {
      setEstado(res);
      setErrorRed(false);
      if (res.irASeleccionMesa && !navegadoRef.current) {
        navegadoRef.current = true;
        stopTimers();
        // Sprint 57 · F4B · el turno conduce a la selección según modalidad.
        const destino =
          evento.modalidad === "ACCESO_GENERAL" ? "acceso-general" : "seleccion-mesa";
        navigate(`/app/eventos/${evento.id}/${destino}`, { replace: true });
        return;
      }
      // Estados terminales fuera de fila → devolver al detalle.
      const terminales = ["COMPLETADA", "CANCELADA", "EXPIRADA", "FUERA"];
      if (terminales.includes(res.estado) && !navegadoRef.current) {
        navegadoRef.current = true;
        stopTimers();
        if (res.motivoTerminacion === "EXPIRADA_TOTAL") {
          toast.warning("Tu turno expiró. Intenta ingresar nuevamente.");
        } else if (res.motivoTerminacion === "EXPIRADA_INACTIVIDAD") {
          toast.warning("Perdiste el turno por inactividad.");
        } else if (res.motivoTerminacion === "COMPLETADA") {
          // ya se navegó a compra confirmada en otro flujo
        }
        navigate(`/app/eventos/${evento.id}`, { replace: true });
        return;
      }
      // Reprograma el siguiente poll según lo sugerido por el backend.
      const seg = Math.max(2, Number(res.intervalPollingSugeridoSegundos || 5));
      pollRef.current = setTimeout(tick, seg * 1000);
    } else {
      setErrorRed(true);
      pollRef.current = setTimeout(tick, 8000);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    if (evento) {
      tick();
    }
    return () => {
      mountedRef.current = false;
      stopTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evento?.id]);

  // Heartbeat sólo mientras el turno esté activo.
  useEffect(() => {
    if (hbRef.current) {
      clearInterval(hbRef.current);
      hbRef.current = null;
    }
    if (!estado || !estado.turnoActivo || !evento) return;
    const seg = Math.max(10, Number(estado.heartbeatSegundos || 30));
    hbRef.current = setInterval(async () => {
      if (!mountedRef.current) return;
      await heartbeatFila(evento.id);
    }, seg * 1000);
    return () => {
      if (hbRef.current) {
        clearInterval(hbRef.current);
        hbRef.current = null;
      }
    };
  }, [estado?.turnoActivo, estado?.heartbeatSegundos, evento]);

  if (!evento) {
    if (!hidratado) return null;
    return <Navigate to="/app" replace />;
  }

  // Cuando el turno está activo el backend indica `irASeleccionMesa` y el
  // tick anterior ya redirigió. Este render sólo se ve mientras hay
  // compradores delante (o mientras se resuelve el primer request).
  if (!estado) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="animate-pulse rounded-2xl border hairline bg-card px-8 py-6 text-sm text-muted-foreground">
          Cargando fila…
        </div>
      </div>
    );
  }

  const salir = async () => {
    stopTimers();
    await cancelarFila(evento.id);
    navigate(`/app/eventos/${evento.id}`);
  };

  const personas = estado.personasDelante ?? 0;
  const titulo = personas === 0 ? "¡Casi es tu turno!" : "Estás en la fila";
  const mensaje = estado.casiEsTuTurno
    ? "Ya casi es tu turno. Mantén esta pantalla abierta."
    : "Te avisaremos aquí en cuanto sea tu turno. Puedes cerrar temporalmente y regresar sin perder tu lugar.";

  return (
    <div
      data-testid={SMART_QUEUE.screen}
      className="min-h-[70vh] flex items-center justify-center"
    >
      <div className="w-full max-w-xl rounded-2xl border hairline bg-card p-8 md:p-10 shadow-soft animate-fade-up text-center">
        <div className="mx-auto w-14 h-14 grid place-items-center rounded-2xl bg-accent/10 ring-1 ring-accent/30 text-accent mb-6">
          <ShieldCheck className="w-6 h-6" strokeWidth={1.5} />
        </div>

        <span className="overline text-accent">Smart Queue</span>
        <h1
          data-testid={SMART_QUEUE.titulo}
          className="mt-3 font-heading text-2xl sm:text-3xl text-foreground"
        >
          {titulo}
        </h1>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="rounded-2xl border hairline bg-background/60 p-6 flex flex-col items-center">
            <span className="overline text-muted-foreground flex items-center gap-1.5">
              <Users className="w-3 h-3" /> Personas delante
            </span>
            <span
              data-testid={SMART_QUEUE.posicion}
              className="mt-2 font-heading text-5xl text-primary dark:text-accent tabular-nums"
            >
              {personas}
            </span>
          </div>
          <div className="rounded-2xl border hairline bg-background/60 p-6 flex flex-col items-center">
            <span className="overline text-muted-foreground flex items-center gap-1.5">
              <Clock className="w-3 h-3" /> Tu posición
            </span>
            <span
              data-testid={SMART_QUEUE.tiempoEstimado}
              className="mt-2 font-heading text-2xl sm:text-3xl text-foreground tabular-nums"
            >
              #{estado.posicion ?? "—"}
            </span>
          </div>
        </div>

        <p
          data-testid={SMART_QUEUE.mensaje}
          className={cn(
            "mt-8 text-sm leading-relaxed max-w-md mx-auto",
            estado.casiEsTuTurno
              ? "text-accent font-medium"
              : "text-muted-foreground"
          )}
        >
          {mensaje}
        </p>

        {errorRed && (
          <p className="mt-4 text-xs text-destructive">
            Sin conexión. Reintentando…
          </p>
        )}

        <button
          type="button"
          data-testid={SMART_QUEUE.btnSalir}
          onClick={salir}
          className={cn(
            "mt-8 inline-flex items-center justify-center gap-2 rounded-full",
            "px-6 py-3 text-sm font-medium",
            "border hairline bg-card text-foreground",
            "transition-all duration-200 active:scale-[0.98] hover:bg-muted"
          )}
        >
          <LogOut className="w-4 h-4" strokeWidth={1.7} />
          Salir de la fila
        </button>
      </div>
    </div>
  );
}
